import { CONTRACT_STATUSES, INVOICE_REVIEW_STATUSES } from '@vy/types';
import { vySerialize, isNotEmptyString, vyDate } from '@vy/utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContracts } from '../../../../hooks/requests/use-contracts';
import { useCustomersOverviews } from '../../../../hooks/requests/use-customers-overviews/use-customers-overviews';
import { useInvoicesOverviews } from '../../../../hooks/requests/use-invoices-overviews/use-invoices-overviews';
import { useEffectOnce } from '../../../../hooks/use-effect-once';
import type { CreateCreditNoteFormValues } from '../create-credit-note-page-components/CreateCreditNoteForm/CreateCreditNoteForm';
import { useCreditItems } from '../../../../hooks/requests/use-credit-items';
import { useQuickBooksProducts } from '../../../../hooks/requests/integrations/quickbooks/use-quickbooks-products';
import { useXeroProducts } from '../../../../hooks/requests/integrations/xero/use-xero-products';
import type { XeroProductPayload } from '@vy/api-definition';

type XeroProductWithCode = XeroProductPayload & { code: string };

function hasXeroProductCode(product: XeroProductPayload): product is XeroProductWithCode {
  return isNotEmptyString(product.code);
}

export const useCreditNoteOptions = () => {
  const isFirstLoad = useFirstLoadState();
  const { watch } = useFormContext<CreateCreditNoteFormValues>();

  const customerId = watch('customerId');
  const selectedContractId = watch('contractId');

  const customersOptions = useCustomerOptions(isFirstLoad);
  const contractsOptions = useContractOptions(customerId, isFirstLoad);
  const invoicesOptions = useInvoiceOptions(customerId, selectedContractId, isFirstLoad);
  const netSuiteProductsOptions = useNetSuiteProductsOptions();
  const quickBooksProductsOptions = useQuickBooksProductsOptions();
  const xeroProductsOptions = useXeroProductsOptions();

  return {
    customersOptions,
    contractsOptions,
    invoicesOptions,
    netSuiteProductsOptions,
    quickBooksProductsOptions,
    xeroProductsOptions,
  };
};

const useFirstLoadState = () => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffectOnce(() => {
    setIsFirstLoad(false);
  });

  return isFirstLoad;
};

const useCustomerOptions = (isFirstLoad: boolean) => {
  const { data: customersOverviewsResponse } = useCustomersOverviews({
    suspense: isFirstLoad,
  });

  return (
    customersOverviewsResponse?.customers.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? []
  );
};

const useContractOptions = (customerId: string | undefined, isFirstLoad: boolean) => {
  const { data: contractsByCustomer } = useContracts(
    { customerId, contractStatuses: vySerialize([CONTRACT_STATUSES.Active]) },
    {
      enabled: isNotEmptyString(customerId),
      suspense: isFirstLoad,
    },
  );

  return (
    contractsByCustomer?.contracts.map(({ id, plan }) => ({
      label: plan.name,
      value: id,
    })) ?? []
  );
};

const useInvoiceOptions = (
  customerId: string | undefined,
  selectedContractId: string | undefined,
  isFirstLoad: boolean,
) => {
  const { data: invoicesByCustomer } = useInvoicesOverviews(
    {
      customerId,
      reviewStatuses: vySerialize([INVOICE_REVIEW_STATUSES.Approved]),
      contractId: selectedContractId,
    },
    {
      enabled: isNotEmptyString(customerId),
      suspense: isFirstLoad,
    },
  );

  return (
    invoicesByCustomer?.invoicesOverviews.map(({ id, contractName, issuedAt }) => {
      const formattedDate = issuedAt && vyDate(issuedAt).format('MMMM, YY');
      const label = isNotEmptyString(contractName) ? `${contractName} - ${formattedDate}` : formattedDate;

      return {
        value: id,
        label: label ?? '',
      };
    }) ?? []
  );
};

const useNetSuiteProductsOptions = () => {
  const { data: creditItems } = useCreditItems();

  return (
    creditItems?.map(({ id, name }) => ({
      value: id,
      label: name,
    })) ?? []
  );
};

const useQuickBooksProductsOptions = () => {
  const { data: quickBooksProductsResponse } = useQuickBooksProducts();

  return (
    quickBooksProductsResponse?.products.map(({ id, name }) => ({
      value: id,
      label: name,
    })) ?? []
  );
};

const useXeroProductsOptions = () => {
  const { data: xeroProductsResponse } = useXeroProducts();

  return (
    xeroProductsResponse?.products.filter(hasXeroProductCode).map(({ code, name }) => ({
      value: code,
      label: name,
    })) ?? []
  );
};
